import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Save, 
  Palette, 
  Type, 
  Layout,
  Square,
  RefreshCw,
  Eye,
  RotateCcw,
  Sparkles,
  Check
} from 'lucide-react';
import { useAuth, API } from '@/App';

// Theme Presets
const themePresets = {
  professional_blue: {
    name: 'Professional Blue',
    description: 'Classic corporate look with deep blue tones',
    colors: {
      primary: '#1F3C4F',
      primary_hover: '#152a38',
      accent: '#3B82F6',
      header_bg: '#FFFFFF',
      header_text: '#475569',
      footer_bg: '#0F172A',
      footer_text: '#94A3B8',
      hero_bg_from: '#F8FAFC',
      hero_bg_to: '#EFF6FF',
      section_alt_bg: '#F8FAFC',
      heading_text: '#0F172A',
      body_text: '#475569',
      muted_text: '#94A3B8',
      link_color: '#1F3C4F',
      button_bg: '#1F3C4F',
      button_text: '#FFFFFF',
      button_secondary_bg: '#FFFFFF',
      button_secondary_text: '#1F3C4F',
      card_bg: '#FFFFFF',
      card_shadow: 'rgba(0,0,0,0.1)'
    }
  },
  modern_teal: {
    name: 'Modern Teal',
    description: 'Fresh and contemporary with teal accents',
    colors: {
      primary: '#0D9488',
      primary_hover: '#0F766E',
      accent: '#14B8A6',
      header_bg: '#FFFFFF',
      header_text: '#334155',
      footer_bg: '#134E4A',
      footer_text: '#99F6E4',
      hero_bg_from: '#F0FDFA',
      hero_bg_to: '#CCFBF1',
      section_alt_bg: '#F0FDFA',
      heading_text: '#0F172A',
      body_text: '#475569',
      muted_text: '#94A3B8',
      link_color: '#0D9488',
      button_bg: '#0D9488',
      button_text: '#FFFFFF',
      button_secondary_bg: '#FFFFFF',
      button_secondary_text: '#0D9488',
      card_bg: '#FFFFFF',
      card_shadow: 'rgba(13,148,136,0.1)'
    }
  },
  elegant_purple: {
    name: 'Elegant Purple',
    description: 'Sophisticated and creative with purple tones',
    colors: {
      primary: '#7C3AED',
      primary_hover: '#6D28D9',
      accent: '#A78BFA',
      header_bg: '#FFFFFF',
      header_text: '#4B5563',
      footer_bg: '#1E1B4B',
      footer_text: '#C4B5FD',
      hero_bg_from: '#FAF5FF',
      hero_bg_to: '#EDE9FE',
      section_alt_bg: '#FAF5FF',
      heading_text: '#1E1B4B',
      body_text: '#4B5563',
      muted_text: '#9CA3AF',
      link_color: '#7C3AED',
      button_bg: '#7C3AED',
      button_text: '#FFFFFF',
      button_secondary_bg: '#FFFFFF',
      button_secondary_text: '#7C3AED',
      card_bg: '#FFFFFF',
      card_shadow: 'rgba(124,58,237,0.1)'
    }
  },
  warm_orange: {
    name: 'Warm Orange',
    description: 'Energetic and inviting with warm tones',
    colors: {
      primary: '#EA580C',
      primary_hover: '#C2410C',
      accent: '#FB923C',
      header_bg: '#FFFFFF',
      header_text: '#44403C',
      footer_bg: '#431407',
      footer_text: '#FED7AA',
      hero_bg_from: '#FFF7ED',
      hero_bg_to: '#FFEDD5',
      section_alt_bg: '#FFF7ED',
      heading_text: '#1C1917',
      body_text: '#57534E',
      muted_text: '#A8A29E',
      link_color: '#EA580C',
      button_bg: '#EA580C',
      button_text: '#FFFFFF',
      button_secondary_bg: '#FFFFFF',
      button_secondary_text: '#EA580C',
      card_bg: '#FFFFFF',
      card_shadow: 'rgba(234,88,12,0.1)'
    }
  },
  forest_green: {
    name: 'Forest Green',
    description: 'Natural and trustworthy with green tones',
    colors: {
      primary: '#166534',
      primary_hover: '#14532D',
      accent: '#22C55E',
      header_bg: '#FFFFFF',
      header_text: '#374151',
      footer_bg: '#052E16',
      footer_text: '#BBF7D0',
      hero_bg_from: '#F0FDF4',
      hero_bg_to: '#DCFCE7',
      section_alt_bg: '#F0FDF4',
      heading_text: '#14532D',
      body_text: '#4B5563',
      muted_text: '#9CA3AF',
      link_color: '#166534',
      button_bg: '#166534',
      button_text: '#FFFFFF',
      button_secondary_bg: '#FFFFFF',
      button_secondary_text: '#166534',
      card_bg: '#FFFFFF',
      card_shadow: 'rgba(22,101,52,0.1)'
    }
  },
  bold_red: {
    name: 'Bold Red',
    description: 'Powerful and attention-grabbing',
    colors: {
      primary: '#DC2626',
      primary_hover: '#B91C1C',
      accent: '#F87171',
      header_bg: '#FFFFFF',
      header_text: '#3F3F46',
      footer_bg: '#450A0A',
      footer_text: '#FECACA',
      hero_bg_from: '#FEF2F2',
      hero_bg_to: '#FEE2E2',
      section_alt_bg: '#FEF2F2',
      heading_text: '#1C1917',
      body_text: '#52525B',
      muted_text: '#A1A1AA',
      link_color: '#DC2626',
      button_bg: '#DC2626',
      button_text: '#FFFFFF',
      button_secondary_bg: '#FFFFFF',
      button_secondary_text: '#DC2626',
      card_bg: '#FFFFFF',
      card_shadow: 'rgba(220,38,38,0.1)'
    }
  },
  slate_minimal: {
    name: 'Slate Minimal',
    description: 'Clean and minimalist monochrome design',
    colors: {
      primary: '#334155',
      primary_hover: '#1E293B',
      accent: '#64748B',
      header_bg: '#FFFFFF',
      header_text: '#475569',
      footer_bg: '#0F172A',
      footer_text: '#94A3B8',
      hero_bg_from: '#F8FAFC',
      hero_bg_to: '#F1F5F9',
      section_alt_bg: '#F8FAFC',
      heading_text: '#0F172A',
      body_text: '#475569',
      muted_text: '#94A3B8',
      link_color: '#334155',
      button_bg: '#334155',
      button_text: '#FFFFFF',
      button_secondary_bg: '#FFFFFF',
      button_secondary_text: '#334155',
      card_bg: '#FFFFFF',
      card_shadow: 'rgba(0,0,0,0.08)'
    }
  },
  ocean_blue: {
    name: 'Ocean Blue',
    description: 'Calm and professional with ocean blue tones',
    colors: {
      primary: '#0369A1',
      primary_hover: '#075985',
      accent: '#38BDF8',
      header_bg: '#FFFFFF',
      header_text: '#475569',
      footer_bg: '#082F49',
      footer_text: '#BAE6FD',
      hero_bg_from: '#F0F9FF',
      hero_bg_to: '#E0F2FE',
      section_alt_bg: '#F0F9FF',
      heading_text: '#0C4A6E',
      body_text: '#475569',
      muted_text: '#94A3B8',
      link_color: '#0369A1',
      button_bg: '#0369A1',
      button_text: '#FFFFFF',
      button_secondary_bg: '#FFFFFF',
      button_secondary_text: '#0369A1',
      card_bg: '#FFFFFF',
      card_shadow: 'rgba(3,105,161,0.1)'
    }
  },
  rose_pink: {
    name: 'Rose Pink',
    description: 'Elegant and feminine with rose tones',
    colors: {
      primary: '#BE185D',
      primary_hover: '#9D174D',
      accent: '#F472B6',
      header_bg: '#FFFFFF',
      header_text: '#4B5563',
      footer_bg: '#500724',
      footer_text: '#FBCFE8',
      hero_bg_from: '#FDF2F8',
      hero_bg_to: '#FCE7F3',
      section_alt_bg: '#FDF2F8',
      heading_text: '#831843',
      body_text: '#4B5563',
      muted_text: '#9CA3AF',
      link_color: '#BE185D',
      button_bg: '#BE185D',
      button_text: '#FFFFFF',
      button_secondary_bg: '#FFFFFF',
      button_secondary_text: '#BE185D',
      card_bg: '#FFFFFF',
      card_shadow: 'rgba(190,24,93,0.1)'
    }
  },
  amber_gold: {
    name: 'Amber Gold',
    description: 'Luxurious and warm with gold accents',
    colors: {
      primary: '#B45309',
      primary_hover: '#92400E',
      accent: '#FBBF24',
      header_bg: '#FFFFFF',
      header_text: '#44403C',
      footer_bg: '#451A03',
      footer_text: '#FDE68A',
      hero_bg_from: '#FFFBEB',
      hero_bg_to: '#FEF3C7',
      section_alt_bg: '#FFFBEB',
      heading_text: '#78350F',
      body_text: '#57534E',
      muted_text: '#A8A29E',
      link_color: '#B45309',
      button_bg: '#B45309',
      button_text: '#FFFFFF',
      button_secondary_bg: '#FFFFFF',
      button_secondary_text: '#B45309',
      card_bg: '#FFFFFF',
      card_shadow: 'rgba(180,83,9,0.1)'
    }
  },
  indigo_night: {
    name: 'Indigo Night',
    description: 'Deep and sophisticated with indigo tones',
    colors: {
      primary: '#4338CA',
      primary_hover: '#3730A3',
      accent: '#818CF8',
      header_bg: '#FFFFFF',
      header_text: '#4B5563',
      footer_bg: '#1E1B4B',
      footer_text: '#C7D2FE',
      hero_bg_from: '#EEF2FF',
      hero_bg_to: '#E0E7FF',
      section_alt_bg: '#EEF2FF',
      heading_text: '#312E81',
      body_text: '#4B5563',
      muted_text: '#9CA3AF',
      link_color: '#4338CA',
      button_bg: '#4338CA',
      button_text: '#FFFFFF',
      button_secondary_bg: '#FFFFFF',
      button_secondary_text: '#4338CA',
      card_bg: '#FFFFFF',
      card_shadow: 'rgba(67,56,202,0.1)'
    }
  },
  emerald_green: {
    name: 'Emerald Green',
    description: 'Fresh and vibrant with emerald tones',
    colors: {
      primary: '#059669',
      primary_hover: '#047857',
      accent: '#34D399',
      header_bg: '#FFFFFF',
      header_text: '#374151',
      footer_bg: '#064E3B',
      footer_text: '#A7F3D0',
      hero_bg_from: '#ECFDF5',
      hero_bg_to: '#D1FAE5',
      section_alt_bg: '#ECFDF5',
      heading_text: '#065F46',
      body_text: '#4B5563',
      muted_text: '#9CA3AF',
      link_color: '#059669',
      button_bg: '#059669',
      button_text: '#FFFFFF',
      button_secondary_bg: '#FFFFFF',
      button_secondary_text: '#059669',
      card_bg: '#FFFFFF',
      card_shadow: 'rgba(5,150,105,0.1)'
    }
  },
  dark_mode: {
    name: 'Dark Mode',
    description: 'Modern dark theme with high contrast',
    colors: {
      primary: '#3B82F6',
      primary_hover: '#2563EB',
      accent: '#60A5FA',
      header_bg: '#1F2937',
      header_text: '#F3F4F6',
      footer_bg: '#111827',
      footer_text: '#9CA3AF',
      hero_bg_from: '#1F2937',
      hero_bg_to: '#111827',
      section_alt_bg: '#1F2937',
      heading_text: '#F9FAFB',
      body_text: '#D1D5DB',
      muted_text: '#9CA3AF',
      link_color: '#60A5FA',
      button_bg: '#3B82F6',
      button_text: '#FFFFFF',
      button_secondary_bg: '#374151',
      button_secondary_text: '#F3F4F6',
      card_bg: '#374151',
      card_shadow: 'rgba(0,0,0,0.3)'
    }
  }
};

const StyleGuidePage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('presets');

  // Default style settings
  const defaultStyles = {
    // Colors
    colors: {
      primary: '#1F3C4F',
      primary_hover: '#152a38',
      accent: '#3B82F6',
      header_bg: '#FFFFFF',
      header_text: '#475569',
      footer_bg: '#0F172A',
      footer_text: '#94A3B8',
      hero_bg_from: '#F8FAFC',
      hero_bg_to: '#EFF6FF',
      section_alt_bg: '#F8FAFC',
      heading_text: '#0F172A',
      body_text: '#475569',
      muted_text: '#94A3B8',
      link_color: '#1F3C4F',
      button_bg: '#1F3C4F',
      button_text: '#FFFFFF',
      button_secondary_bg: '#FFFFFF',
      button_secondary_text: '#1F3C4F',
      card_bg: '#FFFFFF',
      card_shadow: 'rgba(0,0,0,0.1)'
    },
    // Typography
    typography: {
      h1_size: '3.75',
      h1_size_mobile: '2.25',
      h1_weight: '700',
      h2_size: '2.25',
      h2_size_mobile: '1.875',
      h2_weight: '700',
      h3_size: '1.5',
      h3_weight: '600',
      body_size: '1',
      body_weight: '400',
      small_size: '0.875',
      line_height: '1.6'
    },
    // Buttons
    buttons: {
      border_radius: '0.5',
      padding_x: '1.5',
      padding_y: '0.75',
      font_weight: '500',
      style: 'rounded' // rounded, sharp, pill
    },
    // Layout
    layout: {
      logo_height: '5',
      logo_height_mobile: '4',
      header_height: '7',
      section_padding_y: '5',
      section_padding_y_mobile: '3',
      card_padding: '2',
      card_border_radius: '1',
      card_shadow_size: 'medium', // subtle, medium, pronounced
      max_width: '80'
    }
  };

  const [styles, setStyles] = useState(defaultStyles);
  const [selectedPreset, setSelectedPreset] = useState(null);

  useEffect(() => {
    fetchStyles();
  }, []);

  const fetchStyles = async () => {
    try {
      const response = await axios.get(`${API}/style-guide`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && Object.keys(response.data).length > 0) {
        setStyles(prev => ({
          colors: { ...prev.colors, ...response.data.colors },
          typography: { ...prev.typography, ...response.data.typography },
          buttons: { ...prev.buttons, ...response.data.buttons },
          layout: { ...prev.layout, ...response.data.layout }
        }));
      }
    } catch (err) {
      console.log('Using default styles');
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (presetKey) => {
    const preset = themePresets[presetKey];
    if (preset) {
      setStyles(prev => ({
        ...prev,
        colors: { ...prev.colors, ...preset.colors }
      }));
      setSelectedPreset(presetKey);
      toast.success(`Applied "${preset.name}" theme. Click Save to keep changes.`);
    }
  };

  const saveStyles = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/style-guide`, styles, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Style guide saved! Refresh public site to see changes.');
    } catch (err) {
      toast.error('Failed to save style guide');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setStyles(defaultStyles);
    toast.info('Reset to defaults. Click Save to apply.');
  };

  const updateColor = (key, value) => {
    setStyles(prev => ({
      ...prev,
      colors: { ...prev.colors, [key]: value }
    }));
  };

  const updateTypography = (key, value) => {
    setStyles(prev => ({
      ...prev,
      typography: { ...prev.typography, [key]: value }
    }));
  };

  const updateButtons = (key, value) => {
    setStyles(prev => ({
      ...prev,
      buttons: { ...prev.buttons, [key]: value }
    }));
  };

  const updateLayout = (key, value) => {
    setStyles(prev => ({
      ...prev,
      layout: { ...prev.layout, [key]: value }
    }));
  };

  const ColorPicker = ({ label, colorKey, description }) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      {description && <p className="text-xs text-slate-500">{description}</p>}
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={styles.colors[colorKey]}
          onChange={(e) => updateColor(colorKey, e.target.value)}
          className="w-12 h-10 rounded cursor-pointer border border-slate-200"
        />
        <Input
          value={styles.colors[colorKey]}
          onChange={(e) => updateColor(colorKey, e.target.value)}
          className="flex-1 font-mono text-sm"
          placeholder="#000000"
        />
      </div>
    </div>
  );

  const SizeSlider = ({ label, value, onChange, min, max, step, unit, description }) => (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <Label className="text-sm font-medium">{label}</Label>
          {description && <p className="text-xs text-slate-500">{description}</p>}
        </div>
        <span className="text-sm font-mono bg-slate-100 px-2 py-1 rounded">
          {value}{unit}
        </span>
      </div>
      <Slider
        value={[parseFloat(value)]}
        onValueChange={(v) => onChange(v[0].toString())}
        min={min}
        max={max}
        step={step}
        className="w-full"
      />
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3C4F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Style Guide</h1>
          <p className="text-slate-600">Customize colors, typography, and layout for your public site</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetToDefaults}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Defaults
          </Button>
          <a href="/" target="_blank" rel="noopener noreferrer">
            <Button variant="outline">
              <Eye className="w-4 h-4 mr-2" />
              Preview Site
            </Button>
          </a>
          <Button 
            onClick={saveStyles} 
            disabled={saving}
            className="bg-[#1F3C4F] hover:bg-[#152a38]"
          >
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Live Preview Card */}
      <Card className="border-0 shadow-md overflow-hidden">
        <CardHeader className="bg-slate-50 py-3">
          <CardTitle className="text-sm font-medium text-slate-600">Live Preview</CardTitle>
        </CardHeader>
        <CardContent className="p-6" style={{ backgroundColor: styles.colors.hero_bg_from }}>
          <div className="flex items-center gap-8">
            <div 
              className="w-16 rounded flex items-center justify-center text-white text-xs font-bold"
              style={{ 
                height: `${parseFloat(styles.layout.logo_height) * 16}px`,
                backgroundColor: styles.colors.primary 
              }}
            >
              LOGO
            </div>
            <div className="flex-1">
              <h1 
                style={{ 
                  fontSize: `${styles.typography.h1_size}rem`,
                  fontWeight: styles.typography.h1_weight,
                  color: styles.colors.heading_text,
                  lineHeight: '1.1'
                }}
              >
                Sample Heading
              </h1>
              <p 
                style={{ 
                  fontSize: `${styles.typography.body_size}rem`,
                  color: styles.colors.body_text,
                  marginTop: '0.5rem'
                }}
              >
                This is sample body text to preview your typography settings.
              </p>
            </div>
            <button
              style={{
                backgroundColor: styles.colors.button_bg,
                color: styles.colors.button_text,
                padding: `${styles.buttons.padding_y}rem ${styles.buttons.padding_x}rem`,
                borderRadius: styles.buttons.style === 'pill' ? '9999px' : 
                              styles.buttons.style === 'sharp' ? '0' : 
                              `${styles.buttons.border_radius}rem`,
                fontWeight: styles.buttons.font_weight
              }}
            >
              Sample Button
            </button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="presets" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Presets
          </TabsTrigger>
          <TabsTrigger value="colors" className="flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Colors
          </TabsTrigger>
          <TabsTrigger value="typography" className="flex items-center gap-2">
            <Type className="w-4 h-4" />
            Typography
          </TabsTrigger>
          <TabsTrigger value="buttons" className="flex items-center gap-2">
            <Square className="w-4 h-4" />
            Buttons
          </TabsTrigger>
          <TabsTrigger value="layout" className="flex items-center gap-2">
            <Layout className="w-4 h-4" />
            Layout
          </TabsTrigger>
        </TabsList>

        {/* PRESETS TAB */}
        <TabsContent value="presets" className="mt-6">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Theme Presets</CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Choose a preset theme to quickly change your site's look. You can customize further after applying.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(themePresets).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => applyPreset(key)}
                    className={`relative p-4 rounded-xl border-2 text-left transition-all hover:shadow-lg ${
                      selectedPreset === key 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`preset-${key}`}
                  >
                    {selectedPreset === key && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className="flex gap-2 mb-3">
                      <div 
                        className="w-8 h-8 rounded-lg shadow-sm" 
                        style={{ backgroundColor: preset.colors.primary }}
                      />
                      <div 
                        className="w-8 h-8 rounded-lg shadow-sm" 
                        style={{ backgroundColor: preset.colors.accent }}
                      />
                      <div 
                        className="w-8 h-8 rounded-lg shadow-sm" 
                        style={{ backgroundColor: preset.colors.footer_bg }}
                      />
                    </div>
                    <h3 className="font-semibold text-slate-900">{preset.name}</h3>
                    <p className="text-xs text-slate-500 mt-1">{preset.description}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* COLORS TAB */}
        <TabsContent value="colors" className="mt-6 space-y-6">
          {/* Brand Colors */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Brand Colors</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-6">
              <ColorPicker 
                label="Primary Color" 
                colorKey="primary" 
                description="Main brand color for buttons, accents"
              />
              <ColorPicker 
                label="Primary Hover" 
                colorKey="primary_hover" 
                description="Darker shade for hover states"
              />
              <ColorPicker 
                label="Accent Color" 
                colorKey="accent" 
                description="Secondary accent for highlights"
              />
            </CardContent>
          </Card>

          {/* Header & Footer */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Header & Footer</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-6">
              <ColorPicker 
                label="Header Background" 
                colorKey="header_bg" 
              />
              <ColorPicker 
                label="Header Text" 
                colorKey="header_text" 
              />
              <ColorPicker 
                label="Footer Background" 
                colorKey="footer_bg" 
              />
              <ColorPicker 
                label="Footer Text" 
                colorKey="footer_text" 
              />
            </CardContent>
          </Card>

          {/* Section Backgrounds */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Section Backgrounds</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-6">
              <ColorPicker 
                label="Hero Gradient Start" 
                colorKey="hero_bg_from" 
                description="Top of hero gradient"
              />
              <ColorPicker 
                label="Hero Gradient End" 
                colorKey="hero_bg_to" 
                description="Bottom of hero gradient"
              />
              <ColorPicker 
                label="Alternate Section BG" 
                colorKey="section_alt_bg" 
                description="For alternating sections"
              />
            </CardContent>
          </Card>

          {/* Text Colors */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Text Colors</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-6">
              <ColorPicker 
                label="Heading Text" 
                colorKey="heading_text" 
              />
              <ColorPicker 
                label="Body Text" 
                colorKey="body_text" 
              />
              <ColorPicker 
                label="Muted Text" 
                colorKey="muted_text" 
                description="Subtitles, captions"
              />
              <ColorPicker 
                label="Link Color" 
                colorKey="link_color" 
              />
            </CardContent>
          </Card>

          {/* Button & Card Colors */}
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Buttons & Cards</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-6">
              <ColorPicker 
                label="Primary Button Background" 
                colorKey="button_bg" 
                description="Main button background color"
              />
              <ColorPicker 
                label="Primary Button Text" 
                colorKey="button_text" 
                description="Primary button text color"
              />
              <ColorPicker 
                label="Secondary Button BG" 
                colorKey="button_secondary_bg" 
              />
              <ColorPicker 
                label="Secondary Button Text" 
                colorKey="button_secondary_text" 
              />
              <ColorPicker 
                label="Card Background" 
                colorKey="card_bg" 
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* TYPOGRAPHY TAB */}
        <TabsContent value="typography" className="mt-6 space-y-6">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Heading 1 (H1)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <SizeSlider
                label="Desktop Size"
                value={styles.typography.h1_size}
                onChange={(v) => updateTypography('h1_size', v)}
                min={2}
                max={6}
                step={0.25}
                unit="rem"
                description="Main page titles"
              />
              <SizeSlider
                label="Mobile Size"
                value={styles.typography.h1_size_mobile}
                onChange={(v) => updateTypography('h1_size_mobile', v)}
                min={1.5}
                max={4}
                step={0.25}
                unit="rem"
              />
              <div className="space-y-2">
                <Label>Font Weight</Label>
                <Select 
                  value={styles.typography.h1_weight}
                  onValueChange={(v) => updateTypography('h1_weight', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="400">Normal (400)</SelectItem>
                    <SelectItem value="500">Medium (500)</SelectItem>
                    <SelectItem value="600">Semi-Bold (600)</SelectItem>
                    <SelectItem value="700">Bold (700)</SelectItem>
                    <SelectItem value="800">Extra Bold (800)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Heading 2 (H2)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <SizeSlider
                label="Desktop Size"
                value={styles.typography.h2_size}
                onChange={(v) => updateTypography('h2_size', v)}
                min={1.5}
                max={4}
                step={0.125}
                unit="rem"
                description="Section titles"
              />
              <SizeSlider
                label="Mobile Size"
                value={styles.typography.h2_size_mobile}
                onChange={(v) => updateTypography('h2_size_mobile', v)}
                min={1.25}
                max={3}
                step={0.125}
                unit="rem"
              />
              <div className="space-y-2">
                <Label>Font Weight</Label>
                <Select 
                  value={styles.typography.h2_weight}
                  onValueChange={(v) => updateTypography('h2_weight', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="400">Normal (400)</SelectItem>
                    <SelectItem value="500">Medium (500)</SelectItem>
                    <SelectItem value="600">Semi-Bold (600)</SelectItem>
                    <SelectItem value="700">Bold (700)</SelectItem>
                    <SelectItem value="800">Extra Bold (800)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Heading 3 (H3)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <SizeSlider
                label="Size"
                value={styles.typography.h3_size}
                onChange={(v) => updateTypography('h3_size', v)}
                min={1}
                max={2.5}
                step={0.125}
                unit="rem"
                description="Card titles, subsections"
              />
              <div className="space-y-2">
                <Label>Font Weight</Label>
                <Select 
                  value={styles.typography.h3_weight}
                  onValueChange={(v) => updateTypography('h3_weight', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="400">Normal (400)</SelectItem>
                    <SelectItem value="500">Medium (500)</SelectItem>
                    <SelectItem value="600">Semi-Bold (600)</SelectItem>
                    <SelectItem value="700">Bold (700)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Body Text</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <SizeSlider
                label="Body Size"
                value={styles.typography.body_size}
                onChange={(v) => updateTypography('body_size', v)}
                min={0.875}
                max={1.25}
                step={0.0625}
                unit="rem"
              />
              <SizeSlider
                label="Small Text Size"
                value={styles.typography.small_size}
                onChange={(v) => updateTypography('small_size', v)}
                min={0.75}
                max={1}
                step={0.0625}
                unit="rem"
                description="Captions, metadata"
              />
              <SizeSlider
                label="Line Height"
                value={styles.typography.line_height}
                onChange={(v) => updateTypography('line_height', v)}
                min={1.2}
                max={2}
                step={0.1}
                unit=""
                description="Space between lines"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* BUTTONS TAB */}
        <TabsContent value="buttons" className="mt-6 space-y-6">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Button Style</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Corner Style</Label>
                <div className="grid grid-cols-3 gap-4">
                  {['sharp', 'rounded', 'pill'].map((style) => (
                    <button
                      key={style}
                      onClick={() => updateButtons('style', style)}
                      className={`p-4 border-2 rounded-lg transition-all ${
                        styles.buttons.style === style 
                          ? 'border-[#1F3C4F] bg-[#1F3C4F]/5' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div 
                        className="w-full h-10 bg-[#1F3C4F] flex items-center justify-center text-white text-sm"
                        style={{
                          borderRadius: style === 'pill' ? '9999px' : 
                                       style === 'sharp' ? '0' : '0.5rem'
                        }}
                      >
                        {style.charAt(0).toUpperCase() + style.slice(1)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {styles.buttons.style === 'rounded' && (
                <SizeSlider
                  label="Border Radius"
                  value={styles.buttons.border_radius}
                  onChange={(v) => updateButtons('border_radius', v)}
                  min={0.25}
                  max={1.5}
                  step={0.125}
                  unit="rem"
                />
              )}

              <SizeSlider
                label="Horizontal Padding"
                value={styles.buttons.padding_x}
                onChange={(v) => updateButtons('padding_x', v)}
                min={1}
                max={3}
                step={0.25}
                unit="rem"
              />

              <SizeSlider
                label="Vertical Padding"
                value={styles.buttons.padding_y}
                onChange={(v) => updateButtons('padding_y', v)}
                min={0.5}
                max={1.5}
                step={0.125}
                unit="rem"
              />

              <div className="space-y-2">
                <Label>Font Weight</Label>
                <Select 
                  value={styles.buttons.font_weight}
                  onValueChange={(v) => updateButtons('font_weight', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="400">Normal (400)</SelectItem>
                    <SelectItem value="500">Medium (500)</SelectItem>
                    <SelectItem value="600">Semi-Bold (600)</SelectItem>
                    <SelectItem value="700">Bold (700)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LAYOUT TAB */}
        <TabsContent value="layout" className="mt-6 space-y-6">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Logo Size</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <SizeSlider
                label="Desktop Logo Height"
                value={styles.layout.logo_height}
                onChange={(v) => updateLayout('logo_height', v)}
                min={3}
                max={10}
                step={0.5}
                unit="rem"
              />
              <SizeSlider
                label="Mobile Logo Height"
                value={styles.layout.logo_height_mobile}
                onChange={(v) => updateLayout('logo_height_mobile', v)}
                min={2}
                max={6}
                step={0.5}
                unit="rem"
              />
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Header</CardTitle>
            </CardHeader>
            <CardContent>
              <SizeSlider
                label="Header Height"
                value={styles.layout.header_height}
                onChange={(v) => updateLayout('header_height', v)}
                min={4}
                max={10}
                step={0.5}
                unit="rem"
                description="Height of the navigation bar"
              />
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Section Spacing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <SizeSlider
                label="Desktop Section Padding"
                value={styles.layout.section_padding_y}
                onChange={(v) => updateLayout('section_padding_y', v)}
                min={3}
                max={10}
                step={0.5}
                unit="rem"
                description="Vertical padding for content sections"
              />
              <SizeSlider
                label="Mobile Section Padding"
                value={styles.layout.section_padding_y_mobile}
                onChange={(v) => updateLayout('section_padding_y_mobile', v)}
                min={2}
                max={6}
                step={0.5}
                unit="rem"
              />
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg">Cards</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <SizeSlider
                label="Card Padding"
                value={styles.layout.card_padding}
                onChange={(v) => updateLayout('card_padding', v)}
                min={1}
                max={4}
                step={0.25}
                unit="rem"
              />
              <SizeSlider
                label="Card Border Radius"
                value={styles.layout.card_border_radius}
                onChange={(v) => updateLayout('card_border_radius', v)}
                min={0}
                max={2}
                step={0.25}
                unit="rem"
              />
              <div className="space-y-2">
                <Label>Card Shadow</Label>
                <Select 
                  value={styles.layout.card_shadow_size}
                  onValueChange={(v) => updateLayout('card_shadow_size', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="subtle">Subtle</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="pronounced">Pronounced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StyleGuidePage;
